import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import PageHeader from "../../components/PageHeader";
import MessageThread from "../../components/MessageThread";
import { MessageSquare, Loader2, FolderKanban, Search, UserPlus, MailWarning } from "lucide-react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { MSG_REFRESH_EVENT, markNotified } from "../../components/NewMessageNotifier";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { ContactChannels, UnreadBadge } from "../../components/ThreadMeta";
import { CollegaClienteDialog } from "../../components/CollegaClienteDialog";
import { useStoredWidth, ResizeHandle } from "../../components/ResizeHandle";

export default function Messaggi() {
  const [sideW, setSideW] = useStoredWidth("pgp_w_messaggi", 280, 220, 520);
  const [pratiche, setPratiche] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActiveRaw] = useState(null);
  const [meta, setMeta] = useState({});
  const [opened, setOpened] = useState({});
  const setActive = (p) => {
    setActiveRaw(p);
    if (p) setOpened((o) => ({ ...o, [p.id]: true }));
  };
  const [params] = useSearchParams();
  const nav = useNavigate();
  const praticaUrl = (p) => `/app/pratiche?p=${p.id}`;
  const focusId = params.get("p");
  const [q, setQ] = useState("");
  const { user } = useAuth();
  const [linkOpen, setLinkOpen] = useState(false);
  const markUnread = async () => {
    const id = active.id;
    try {
      const { data } = await api.post(`/studio/pratiche/${id}/messages/mark-unread`);
      setMeta((m) => ({ ...m, [id]: { ...(m[id] || {}), unread: data.unread } }));
      markNotified(user?.id, data.message_id);
      setOpened((o) => ({ ...o, [id]: false }));
      setActiveRaw(null);
      window.dispatchEvent(new Event(MSG_REFRESH_EVENT));
      toast.success("Conversazione segnata come non letta");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onLinked = (cli, ch) => {
    setMeta((m) => ({ ...m, [active.id]: { ...(m[active.id] || {}), ...ch } }));
    const upd = { ...active, cliente_nome: active.cliente_nome || cli.nome };
    setActiveRaw(upd);
    setPratiche((ps) => ps.map((p) => (p.id === upd.id ? upd : p)));
  };
  const needle = q.trim().toLowerCase();
  const tokens = needle.split(/\s+/).filter(Boolean);
  const subjectTexts = (p) => {
    const nameOf = (s) => (s && typeof s === "object"
      ? [s.cognome, s.nome, s.ragione_sociale, s.denominazione, s.nome_completo].filter(Boolean).join(" ") : String(s || ""));
    return [p.nome_assistito, p.assistito_ragione_sociale, p.cliente_nome, nameOf(p.assistito_scheda),
      ...(p.assistiti || []).map(nameOf)]
      .map((v) => String(v || "").toLowerCase()).filter(Boolean);
  };
  const matches = (p) => String(p.numero_pratica || "").toLowerCase().includes(needle)
    || subjectTexts(p).some((t) => tokens.every((tk) => t.includes(tk)));
  const unreadOf = (p) => (opened[p.id] ? 0 : meta[p.id]?.unread || 0);
  const rank = (p) => [unreadOf(p) > 0 ? 1 : 0, meta[p.id]?.last_received_at || "", meta[p.id]?.last_message_at || ""];
  const filtered = (needle ? pratiche.filter(matches) : pratiche)
    .map((p, i) => ({ p, i, r: rank(p) }))
    .sort((a, b) => (b.r[0] - a.r[0]) || b.r[1].localeCompare(a.r[1]) || b.r[2].localeCompare(a.r[2]) || (a.i - b.i))
    .map((x) => x.p);

  useEffect(() => {
    api.get("/studio/pratiche")
      .then(({ data }) => setPratiche(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
    api.get("/studio/messaggi/overview")
      .then(({ data }) => setMeta(data))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const p = focusId && pratiche.find((x) => x.id === focusId);
    if (p) setActive(p);
    else if (focusId && pratiche.length && !active) setActive(pratiche[0]);
  }, [focusId, pratiche]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="messaggi-page">
      <PageHeader
        title="Messaggistica"
        subtitle="Comunica con i clienti. Ogni conversazione è collegata a una pratica."
        icon={MessageSquare}
      />

      {loading ? (
        <div className="pgp-card p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
      ) : pratiche.length === 0 ? (
        <div className="pgp-card p-10 text-center text-slate-400 text-sm">Nessuna pratica disponibile. Crea prima una pratica.</div>
      ) : (
        <div className="grid lg:grid-cols-[var(--side-w)_minmax(0,1fr)] gap-4 2xl:gap-6" style={{ "--side-w": `${sideW}px` }}>
          <div className="relative h-fit">
          <ResizeHandle width={sideW} onChange={setSideW} testId="messaggi-side-resize-handle" className="hidden lg:block" />
          <div className="pgp-card p-2 h-fit max-h-[70vh] overflow-y-auto pgp-fade-up">
            <div className="sticky top-0 z-10 bg-inherit pb-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente o n. pratica"
                  data-testid="messaggi-search-input"
                  className="w-full h-9 rounded-lg border border-slate-700 bg-transparent pl-8 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-amber-500 transition-colors" />
              </div>
            </div>
            {filtered.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-slate-500" data-testid="messaggi-search-empty">Nessuna conversazione trovata.</div>
            )}
            {filtered.map((p) => (
              <button key={p.id} onClick={() => setActive(p)} data-testid={`thread-item-${p.id}`}
                className={`w-full text-left rounded-lg px-3 py-3 mb-1 transition-colors ${active?.id === p.id ? "bg-amber-500/15 border border-amber-500/30" : "hover:bg-slate-800/60 border border-transparent"}`}>
                <div className={`flex items-center gap-2 text-sm text-slate-100 ${unreadOf(p) > 0 ? "font-bold" : "font-medium"}`} data-testid={`thread-name-${p.id}`}>
                  <FolderKanban className="h-4 w-4 text-slate-400" /> {p.nome_assistito}
                  <UnreadBadge count={unreadOf(p)} id={p.id} />
                </div>
                <ContactChannels email={meta[p.id]?.email} whatsapp={meta[p.id]?.whatsapp} cellulare={meta[p.id]?.cellulare} id={p.id} />
                <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                  <span role="link" tabIndex={0} title="Apri la scheda pratica" data-testid={`thread-pratica-link-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); nav(praticaUrl(p)); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); e.preventDefault(); nav(praticaUrl(p)); } }}
                    className="underline-offset-2 hover:underline hover:text-amber-500 cursor-pointer transition-colors">{p.numero_pratica}</span> · {formatDate(p.data_sinistro)}
                </div>
              </button>
            ))}
          </div>
          </div>

          <div className="pgp-card p-5 pgp-fade-up">
            {active ? (
              <>
                <div className="mb-3 pb-3 border-b border-slate-800 flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="font-head font-semibold text-slate-100">
                      <Link to={praticaUrl(active)} title="Apri la scheda pratica" data-testid="thread-header-pratica-link"
                        className="underline-offset-2 hover:underline hover:text-amber-500 transition-colors">{active.numero_pratica}</Link> · {active.nome_assistito}
                    </div>
                    <div className="text-xs text-slate-500" data-testid="thread-cliente-label">{meta[active.id]?.portal_names?.length || active.cliente_nome ? `Cliente: ${meta[active.id]?.portal_names?.length ? meta[active.id].portal_names.join(", ") : active.cliente_nome}` : "Nessun cliente collegato"}</div>
                  </div>
                  {meta[active.id]?.last_received_at && (
                    <button type="button" onClick={markUnread} data-testid="thread-mark-unread"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-[#111827] hover:bg-[#F3F4F6] transition-colors">
                      <MailWarning className="h-3.5 w-3.5" /> Segna come non letto
                    </button>
                  )}
                </div>
                <MessageThread
                  key={active.id}
                  getUrl={`/studio/pratiche/${active.id}/messages`}
                  postUrl={`/studio/pratiche/${active.id}/messages`}
                  selfRole="studio"
                  onLoaded={() => window.dispatchEvent(new Event(MSG_REFRESH_EVENT))}
                  showReadStatus
                  canDelete
                  blockedReason={meta[active.id] && !meta[active.id].email && !meta[active.id].whatsapp && !meta[active.id].portal
                    ? "Nessun cliente collegato a questa pratica. Associa un cliente con email o cellulare per inviare messaggi." : ""}
                  blockedAction={
                    <button type="button" onClick={() => setLinkOpen(true)} data-testid="message-link-client-button"
                      className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 hover:bg-amber-600 px-3 py-1.5 text-xs font-semibold text-[#0a0d14] transition-colors">
                      <UserPlus className="h-3.5 w-3.5" /> Collega cliente
                    </button>
                  }
                />
                <CollegaClienteDialog open={linkOpen} onOpenChange={setLinkOpen} pratica={active} onLinked={onLinked} />
              </>
            ) : (
              <div className="text-sm text-slate-500">Seleziona una pratica.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
